import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/base/MovieCard";
import ShareModal from "@/pages/detail/components/ShareModal";
import WatchModal from "@/pages/detail/components/WatchModal";
import { formatHeat, getMediaById, getRelated, typeMeta } from "@/lib/media";
import { usePageMeta } from "@/hooks/usePageMeta";

const getInitial = (name: string) => {
  const cleaned = name.split("·").pop()?.trim() || name;
  return cleaned.charAt(0);
};

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = getMediaById(id);
  const [shareOpen, setShareOpen] = useState(false);
  const [watchOpen, setWatchOpen] = useState(false);

  usePageMeta({
    title: item
      ? `${item.title} - ${typeMeta[item.type].label}详情 | 蜜桃33`
      : "影视资源不存在 | 蜜桃33",
    description: item
      ? `蜜桃33提供${item.title}的详细剧情介绍、演员资料及相关推荐，影视爱好者必备。`
      : "该影视资源不存在，返回蜜桃33首页继续浏览其他影视作品。",
    keywords: item
      ? `蜜桃33,${item.title},${typeMeta[item.type].label},${item.genres.join(",")},影视详情`
      : "蜜桃33,影视资源",
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  if (!item) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="flex w-full flex-1 items-center justify-center px-4 py-20">
          <div className="w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-8 text-center">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-50 text-primary-500">
              <i className="ri-file-unknow-line text-3xl" />
            </span>
            <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">影视资源不存在</h1>
            <p className="mt-2 text-sm leading-relaxed text-foreground-500">
              你访问的影视 ID 没有对应内容，可能是链接有误或资源已下架。可以返回首页重新浏览。
            </p>
            <div className="mt-6 flex flex-col justify-center gap-2 sm:flex-row">
              <Link
                to="/"
                className="inline-flex h-10 cursor-pointer items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                返回首页
              </Link>
              <Link
                to="/category/movie"
                className="inline-flex h-10 cursor-pointer items-center justify-center whitespace-nowrap rounded-md border border-background-300 px-5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                浏览电影专区
              </Link>
            </div>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = getRelated(item, 4);
  const castLabel = item.type === "variety" ? "主创 / 主持" : "主演";
  const infoRows = [
    { label: "导演", value: item.director },
    { label: castLabel, value: item.cast.join(" / ") },
    { label: "类型", value: item.genres.join(" · ") },
    { label: item.type === "movie" ? "上映年份" : "开播年份", value: `${item.year}` },
    { label: "地区", value: item.region },
    { label: item.type === "movie" ? "片长" : "集数", value: item.episodes },
  ];

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="w-full flex-1">
        <div className="border-b border-background-200 bg-background-100 px-4 py-3 md:px-8">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-300" />
            <Link to={`/category/${item.type}`} className="transition-colors hover:text-primary-600">
              {typeMeta[item.type].label}
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-300" />
            <span className="line-clamp-1 text-foreground-800">{item.title}</span>
          </nav>
        </div>

        <section className="relative w-full overflow-hidden bg-foreground-950">
          <img
            src={item.cover}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full scale-110 object-cover object-top opacity-25 blur-2xl"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950 via-foreground-950/85 to-foreground-950/60" />

          <div className="relative w-full px-4 py-8 md:px-8 md:py-12">
            <div className="flex flex-col gap-7 md:flex-row md:gap-9">
              <div className="w-[132px] shrink-0 sm:w-[168px] md:w-[220px]">
                <div className="aspect-[5/7] w-full overflow-hidden rounded-lg border border-background-50/20">
                  <img
                    src={item.cover}
                    alt={`${item.title} 海报`}
                    title={`${item.title} - 蜜桃33影视详情`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="mt-3 rounded-md border border-background-50/15 bg-background-50/5 px-3 py-2 text-center">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-background-300/60">Rating</p>
                  <p className="mt-1 font-heading text-xl font-bold text-accent-300">
                    {item.rating.toFixed(1)}
                  </p>
                  <p className="text-[10px] text-background-300/60">虚构评分</p>
                </div>
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-md bg-primary-500 px-2.5 py-1 text-[11px] font-medium text-background-50">
                    {typeMeta[item.type].label}
                  </span>
                  {item.genres.map((genre) => (
                    <Link
                      key={genre}
                      to={`/category/${item.type}?genre=${encodeURIComponent(genre)}`}
                      className="rounded-md border border-background-50/20 px-2.5 py-1 text-[11px] text-background-200/85 transition-colors hover:border-accent-400 hover:text-accent-300"
                    >
                      {genre}
                    </Link>
                  ))}
                </div>

                <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-background-50 md:text-[42px]">
                  {item.title}
                </h1>
                <p className="mt-2 text-[11px] uppercase tracking-[0.28em] text-background-300/60">
                  {item.originalTitle}
                </p>
                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-100/85 md:text-[15px]">
                  {item.tagline}
                </p>

                <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 text-xs sm:grid-cols-2 lg:max-w-3xl">
                  {infoRows.map((row) => (
                    <div key={row.label} className="flex gap-3 border-b border-background-50/10 pb-2.5">
                      <dt className="w-16 shrink-0 text-background-300/60">{row.label}</dt>
                      <dd className="flex-1 text-background-100/90">{row.value}</dd>
                    </div>
                  ))}
                </dl>

                <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <button
                    type="button"
                    onClick={() => setWatchOpen(true)}
                    className="inline-flex h-11 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-play-circle-line text-base" />
                    在线观看
                    <span className="rounded bg-background-50/20 px-1.5 py-0.5 text-[10px]">演示</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShareOpen(true)}
                    className="inline-flex h-11 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-6 text-sm text-background-100 transition-colors hover:bg-background-50/10"
                  >
                    <i className="ri-share-forward-line text-base" />
                    分享
                  </button>
                  <span className="flex items-center gap-1.5 text-[11px] text-background-300/60 sm:ml-2">
                    <i className="ri-fire-line text-accent-400" />
                    站内热度 {formatHeat(item.heat)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="w-full px-4 py-10 md:px-8">
          <div className="grid gap-10 lg:grid-cols-[1fr_300px] lg:gap-14">
            <div className="min-w-0">
              <article>
                <div className="flex items-center gap-2">
                  <span className="h-4 w-1 rounded-full bg-primary-500" />
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    剧情介绍
                  </h2>
                </div>
                <p className="mt-4 rounded-md border-l-2 border-accent-400 bg-accent-50/60 px-4 py-3 text-sm leading-relaxed text-foreground-700">
                  {item.summary}
                </p>
                <p className="mt-5 text-[15px] leading-[2] text-foreground-700">{item.synopsis}</p>
              </article>

              <section className="mt-10">
                <div className="flex items-center gap-2">
                  <span className="h-4 w-1 rounded-full bg-primary-500" />
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    {castLabel}
                  </h2>
                </div>
                <div className="mt-5 flex flex-wrap gap-4">
                  <div className="flex w-24 flex-col items-center gap-2 text-center">
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 font-heading text-xl font-bold text-primary-700">
                      {getInitial(item.director)}
                    </span>
                    <span className="text-xs font-medium text-foreground-800">{item.director}</span>
                    <span className="text-[10px] text-foreground-400">导演</span>
                  </div>
                  {item.cast.map((person) => (
                    <div key={person} className="flex w-24 flex-col items-center gap-2 text-center">
                      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 font-heading text-xl font-bold text-secondary-800">
                        {getInitial(person)}
                      </span>
                      <span className="line-clamp-2 text-xs font-medium text-foreground-800">
                        {person}
                      </span>
                      <span className="text-[10px] text-foreground-400">{castLabel}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-[11px] text-foreground-400">
                  以上主创姓名与角色均为本站虚构演示内容，不代表任何真实演职人员。
                </p>
              </section>

              <section className="mt-10">
                <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="h-4 w-1 rounded-full bg-primary-500" />
                    <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                      相关推荐
                    </h2>
                  </div>
                  <p className="text-xs text-foreground-400">依据类型与标签匹配推荐</p>
                </div>
                {related.length > 0 ? (
                  <div className="grid grid-cols-2 gap-x-3 gap-y-7 sm:grid-cols-3 md:gap-x-5 lg:grid-cols-4">
                    {related.map((entry) => (
                      <MovieCard key={entry.id} item={entry} variant="compact" />
                    ))}
                  </div>
                ) : (
                  <p className="rounded-md bg-background-100 px-4 py-8 text-center text-sm text-foreground-500">
                    暂时没有找到相似的影视作品，去分类页看看其他内容吧。
                  </p>
                )}
              </section>
            </div>

            <aside className="lg:sticky lg:top-24 lg:self-start">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="flex items-center gap-1.5 text-sm font-semibold text-foreground-900">
                  <i className="ri-folder-open-line text-primary-600" />
                  所属分类
                </h2>
                <div className="mt-3 flex flex-col gap-2">
                  <Link
                    to={`/category/${item.type}`}
                    className="flex items-center justify-between rounded-md bg-background-100 px-3 py-2.5 text-xs text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                  >
                    {typeMeta[item.type].subtitle}
                    <i className="ri-arrow-right-s-line" />
                  </Link>
                  {item.genres.map((genre) => (
                    <Link
                      key={genre}
                      to={`/category/${item.type}?genre=${encodeURIComponent(genre)}`}
                      className="flex items-center justify-between rounded-md bg-background-100 px-3 py-2.5 text-xs text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                    >
                      {typeMeta[item.type].label} · {genre}
                      <i className="ri-arrow-right-s-line" />
                    </Link>
                  ))}
                  <Link
                    to={`/category/${item.type}?region=${encodeURIComponent(item.region)}`}
                    className="flex items-center justify-between rounded-md bg-background-100 px-3 py-2.5 text-xs text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                  >
                    {item.region}作品
                    <i className="ri-arrow-right-s-line" />
                  </Link>
                </div>
              </div>

              <div className="mt-4 rounded-lg bg-accent-50 p-5">
                <h2 className="flex items-center gap-1.5 text-sm font-semibold text-accent-900">
                  <i className="ri-information-line" />
                  演示说明
                </h2>
                <ul className="mt-3 space-y-2 text-[11px] leading-relaxed text-foreground-600">
                  <li className="flex gap-2">
                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-accent-500" />
                    影视资料、评分与主创均为虚构示例。
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-accent-500" />
                    暂未接入真实片源，不提供在线播放。
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-accent-500" />
                    分享与观看按钮均为前端模拟交互。
                  </li>
                </ul>
              </div>

              <div className="mt-4 rounded-lg border border-background-200 p-5">
                <h2 className="text-sm font-semibold text-foreground-900">标签</h2>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {item.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] text-secondary-900"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <ShareModal open={shareOpen} title={item.title} onClose={() => setShareOpen(false)} />
      <WatchModal open={watchOpen} item={item} onClose={() => setWatchOpen(false)} />

      <SiteFooter />
    </div>
  );
}